import React, { useState, useCallback } from 'react';
import { useAlbumTilePlayback } from '../hooks/useAlbumTilePlayback';
import { TransportCommand } from '../transport/TransportCommand';

const GOLD = '#C9A84C';

function AlbumPlayButtonView({
  onPlay,
  onPause,
  isActive = false,
  isPlaying = false,
  disabled = false,
  className = '',
}) {
  const [loading, setLoading] = useState(false);

  const handleClick = useCallback(async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (loading || disabled) return;
    if (isPlaying) {
      try {
        await (onPause?.() ?? TransportCommand.pause(true));
      } catch {
        /* pause best-effort */
      }
      return;
    }
    setLoading(true);
    try {
      await onPlay?.();
    } finally {
      setLoading(false);
    }
  }, [onPlay, onPause, loading, disabled, isPlaying]);

  if (disabled) return null;

  const visibilityClass = isActive
    ? 'opacity-100'
    : 'opacity-100 pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100 pointer-coarse:opacity-100';

  return (
    <div
      aria-hidden="true"
      className={`transition-opacity duration-150 ${visibilityClass} ${className}`.trim()}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        background: 'rgba(0,0,0,0.35)',
      }}
    >
      <button
        type="button"
        aria-label={isPlaying ? 'Pause album' : 'Play album'}
        title={isPlaying ? 'Pause' : 'Play'}
        onClick={handleClick}
        disabled={loading}
        style={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          background: loading ? 'rgba(201,168,76,0.6)' : `linear-gradient(135deg, ${GOLD}, #d4a843)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 4px 16px ${GOLD}40`,
          border: 'none',
          cursor: loading ? 'wait' : 'pointer',
          padding: 0,
          pointerEvents: 'auto',
          flexShrink: 0,
        }}
      >
        {loading ? (
          <span
            style={{
              width: 18,
              height: 18,
              border: '2px solid rgba(0,0,0,0.2)',
              borderTopColor: '#0a0a0f',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }}
          />
        ) : isPlaying ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="#0a0a0f" aria-hidden="true">
            <path d="M6 19H10V5H6V19ZM14 5V19H18V5H14Z" />
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="#0a0a0f" aria-hidden="true">
            <path d="M5 4l16 8-16 8V4z" />
          </svg>
        )}
      </button>
    </div>
  );
}

function AlbumPlayButtonConnected({ album, ...rest }) {
  const { isActive, isPlaying } = useAlbumTilePlayback(album);
  return (
    <AlbumPlayButtonView
      {...rest}
      isActive={rest.isActive ?? isActive}
      isPlaying={rest.isPlaying ?? isPlaying}
    />
  );
}

export default function AlbumPlayButton({
  album,
  isActive,
  isPlaying,
  ...rest
}) {
  if (isActive !== undefined || isPlaying !== undefined) {
    return (
      <AlbumPlayButtonView
        {...rest}
        isActive={isActive ?? false}
        isPlaying={isPlaying ?? false}
      />
    );
  }
  return <AlbumPlayButtonConnected album={album} {...rest} />;
}
